import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { PageDialog } from "./ReferenceUI";

type NotificationItem = {
  id: string;
  kind: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
  action: { tab: "likes" | "matches" | "chats" | "profile" } | null;
};

type NotificationPage = {
  notifications: NotificationItem[];
  nextCursor: string | null;
};

const iconByKind: Record<string, string> = {
  like: "♡",
  superlike: "★",
  team: "◇",
  match: "♥",
  message: "✉",
  moderation_photo_approved: "✓",
  moderation_photo_rejected: "!",
  moderation_account_frozen: "!",
  moderation_account_banned: "!",
  moderation_account_restored: "✓",
};

export function NotificationCenter({
  onClose,
  onNavigate,
  onUnreadChanged,
  onSettings,
}: {
  onClose(): void;
  onNavigate(tab: "likes" | "matches" | "chats" | "profile"): void;
  onUnreadChanged(): Promise<void>;
  onSettings(): void;
}) {
  const [items, setItems] = useState<NotificationItem[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState("");

  const readAll = useCallback(async () => {
    setMarkingAll(true);
    setActionError("");
    try {
      await api("/v1/notifications/read-all", { method: "POST" });
      setItems((current) => current?.map((item) => ({ ...item, read: true })) ?? null);
      // A badge refresh failure must never erase the loaded list.
      await onUnreadChanged().catch(() => undefined);
    } catch {
      setActionError("Не удалось отметить уведомления прочитанными. Попробуйте ещё раз.");
    } finally {
      setMarkingAll(false);
    }
  }, [onUnreadChanged]);

  const loadInitial = useCallback(async () => {
    setItems(null);
    setLoadError(false);
    setActionError("");
    try {
      const result = await api<NotificationPage>("/v1/notifications?limit=30");
      setItems(result.notifications);
      setNextCursor(result.nextCursor);
      if (result.notifications.some((item) => !item.read)) void readAll();
    } catch {
      setItems([]);
      setNextCursor(null);
      setLoadError(true);
    }
  }, [readAll]);

  useEffect(() => {
    void loadInitial();
  }, [loadInitial]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setActionError("");
    try {
      const result = await api<NotificationPage>(`/v1/notifications?limit=30&before=${encodeURIComponent(nextCursor)}`);
      setItems((current) => [...(current ?? []), ...result.notifications]);
      setNextCursor(result.nextCursor);
    } catch {
      setActionError("Не удалось загрузить следующую страницу. Нажмите «Показать ещё», чтобы повторить.");
    } finally {
      setLoadingMore(false);
    }
  };

  const readOne = async (item: NotificationItem) => {
    if (!item.read) {
      try {
        await api(`/v1/notifications/${item.id}/read`, { method: "POST" });
        setItems((current) => current?.map((entry) => (entry.id === item.id ? { ...entry, read: true } : entry)) ?? []);
        await onUnreadChanged().catch(() => undefined);
      } catch {
        setActionError("Не удалось сохранить отметку о прочтении.");
      }
    }

    if (item.action) {
      onNavigate(item.action.tab);
    }
  };

  const hasUnread = items?.some((item) => !item.read) ?? false;

  return (
    <PageDialog
      title="Уведомления"
      onClose={onClose}
      action={
        <button type="button" aria-label="Настройки уведомлений" onClick={onSettings}>
          ⚙
        </button>
      }
    >
      {hasUnread && (
        <button type="button" className="mark-all-read" disabled={markingAll} onClick={() => void readAll()}>
          {markingAll ? "Сохраняем…" : "Отметить всё прочитанным"}
        </button>
      )}

      {actionError && (
        <p className="reference-error" role="alert">
          {actionError}
        </p>
      )}
      <div className="notification-list">
        {items === null ? (
          <p className="reference-note" role="status">
            Загружаем уведомления…
          </p>
        ) : loadError ? (
          <div className="notification-empty">
            <span>!</span>
            <strong>Не удалось загрузить уведомления</strong>
            <p>Проверьте соединение и повторите попытку.</p>
            <button type="button" className="secondary-button" onClick={() => void loadInitial()}>
              Повторить
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="notification-empty">
            <span>✓</span>
            <strong>Здесь пока тихо</strong>
            <p>Совпадения, сообщения и важные статусы появятся здесь.</p>
          </div>
        ) : (
          items.map((item) => (
            <button
              type="button"
              className={item.read ? "notification-row" : "notification-row unread"}
              key={item.id}
              onClick={() => {
                void readOne(item);
              }}
            >
              <span className="notification-icon" aria-hidden="true">
                {iconByKind[item.kind] ?? "•"}
              </span>
              <span className="notification-copy">
                <span className="notification-title-line">
                  <strong>{item.title}</strong>
                  {!item.read && (
                    <>
                      <span className="sr-only">Непрочитано</span>
                      <span className="notification-unread-dot" aria-hidden="true" />
                    </>
                  )}
                </span>
                <span>{item.body}</span>
                <time dateTime={item.createdAt}>{new Date(item.createdAt).toLocaleString("ru-RU")}</time>
              </span>
              {item.action && (
                <span className="notification-chevron" aria-hidden="true">
                  ›
                </span>
              )}
            </button>
          ))
        )}

        {nextCursor && (
          <button type="button" className="load-more-conversations" disabled={loadingMore} onClick={loadMore}>
            {loadingMore ? "Загружаем…" : "Показать ещё"}
          </button>
        )}
      </div>
    </PageDialog>
  );
}
